import type { HelpCategory } from "../../types";

export const analytics: HelpCategory = {
  id: "analytics",
  title: "Аналитика и финансы",
  description: "Дашборд, отчёты о продажах, аналитика кафе, расходы, защита от злоупотреблений и Coca AI.",
  icon: "chart",
  articles: [
    {
      id: "dashboard-reports",
      title: "Дашборд и отчёт о продажах",
      summary: "Главные цифры дня на дашборде и отчёт о продажах за период: выручка, заказы, средний чек, по часам и дням, топ блюд.",
      path: "/reports",
      feature: "core.analytics_basic",
      permission: "reports.view",
      keywords: ["дашборд", "отчёт", "выручка", "средний чек", "продажи", "топ блюд"],
      related: ["insights", "expenses-finance"],
      body: [
        { t: "video", id: "reports", title: "Отчёты и аналитика", duration: 25 },
        { t: "p", text: "[«Дашборд»](/dashboard) — сводка: выручка сегодня, число заказов, средний чек, загрузка по часам." },
        { t: "p", text: "[«Отчёты»](/reports) — за любой период: **выручка = оплаты − возвраты**, заказы, средний чек, графики по часам или дням (в часовом поясе филиала), топ позиций, продажи с изменённой ценой." },
        { t: "warn", text: "Глубина истории зависит от тарифа (например, 90 дней на Start). Отчёты недоступны, когда подписка в режиме «только чтение»." },
      ],
    },
    {
      id: "insights",
      title: "Аналитика кафе и расширенные отчёты",
      summary: "Прибыль, продажи блюд, расход продуктов, списания и официанты; с дополнением — ABC-анализ, меню-инжиниринг, food cost и тепловая карта.",
      path: "/analytics",
      feature: "core.analytics_basic",
      permission: "reports.view",
      keywords: ["аналитика", "прибыль", "abc", "меню-инжиниринг", "food cost", "тепловая карта", "официанты"],
      related: ["dashboard-reports", "costing", "expenses-finance"],
      body: [
        {
          t: "terms", title: "Вкладки",
          items: [
            { term: "Финансы", text: "Выручка, себестоимость, расходы, прибыль (с дополнением «Финансы»)." },
            { term: "Меню", text: "Что продаётся, по сколько, с какой маржой." },
            { term: "Продукты", text: "Сколько продуктов ушло по техкартам (теоретический расход)." },
            { term: "Списания", text: "По причинам и продуктам." },
            { term: "Официанты", text: "Выручка, чеки и средний чек по сотрудникам." },
            { term: "ABC (расширенные)", text: "Какие 20% позиций дают 80% выручки или маржи." },
            { term: "Матрица меню (расширенные)", text: "Звёзды, рабочие лошадки, загадки и аутсайдеры — что продвигать, что убрать." },
            { term: "Food & labor cost (расширенные)", text: "Себестоимость продуктов и труда по дням; выручка на час работы." },
            { term: "Тепловая карта (расширенные)", text: "Загрузка по дням недели и часам." },
          ],
        },
        { t: "tip", text: "Если в аналитике предупреждение «Не у всех блюд есть ТТК» — себестоимость занижена. Заполните техкарты: [[recipes]]. Таблицы выгружаются в CSV для Excel." },
      ],
    },
    {
      id: "expenses-finance",
      title: "Расходы и финансы",
      summary: "Аренда, коммунальные, маркетинг и прочие расходы; как они вместе с закупками, зарплатой и долгами складываются в прибыль и движение денег.",
      path: "/expenses",
      feature: "addon.finance",
      permission: "expenses.view",
      keywords: ["расходы", "аренда", "коммунальные", "прибыль", "p&l", "движение денег"],
      related: ["insights", "payroll", "suppliers"],
      body: [
        { t: "steps", items: ["[«Расходы»](/expenses) → **«Добавить расход»**: категория, сумма, дата, способ оплаты, комментарий.", "Закупки продуктов сюда не вносятся — они в разделе «Закупки»."] },
        {
          t: "list", title: "Из чего складывается прибыль",
          items: ["Выручка (оплаты − возвраты).", "− себестоимость проданного (по техкартам).", "− зарплата из утверждённых ведомостей.", "− расходы.", "Движение денег отдельно показывает закупки, оплаты поставщикам, погашения долгов гостей, депозиты банкетов."],
        },
      ],
    },
    {
      id: "fraud",
      title: "Защита от злоупотреблений",
      summary: "Система каждый час ищет подозрительные действия: удаления после кухни, возвраты наличными за карту, лишние скидки, недостачи. Вы решаете, что с ними делать.",
      path: "/fraud",
      feature: "addon.fraud",
      permission: "fraud.view",
      keywords: ["фрод", "злоупотребления", "воровство", "подозрительные", "недостача", "риск"],
      related: ["cash-shifts", "pos-discounts", "audit"],
      body: [
        {
          t: "list", title: "Что проверяется",
          items: ["Удаления позиций и отмены после отправки на кухню.", "Частые возвраты; возврат наличными за оплату картой.", "Высокая доля ручных скидок, продажа ниже цены меню.", "Списания выше обычного уровня.", "Недостачи в кассе и их повторы.", "Долги без погашений."],
        },
        {
          t: "steps", title: "Работа с сигналами",
          items: [
            "[«Защита от злоупотреблений»](/fraud): сигналы по важности, «Кто чаще попадает в сигналы» — баллы риска сотрудников.",
            "Откройте сигнал → решение: **проверено**, **ложная тревога** или **подтверждено** + комментарий.",
            "Пороги правил — в настройках раздела.",
          ],
        },
        { t: "tip", text: "О серьёзных сигналах приходит уведомление сразу, по понедельникам — недельная сводка." },
      ],
    },
    {
      id: "ai",
      title: "Coca AI — помощник",
      summary: "Задайте вопрос о своём заведении обычными словами — помощник ответит по вашим отчётам: продажи, меню, официанты, списания, склад.",
      path: "/ai",
      keywords: ["ai", "ии", "помощник", "чат", "искусственный интеллект"],
      related: ["dashboard-reports", "insights"],
      body: [
        { t: "p", text: "Кнопка **AI** в шапке открывает помощника. Спрашивайте как управляющего: «Почему во вторник выручка ниже?», «Какие блюда хуже всего продаются?», «Что заканчивается на складе?»." },
        { t: "list", items: ["Помощник видит только те отчёты, на которые у вас есть права и функции тарифа.", "Ответ — по данным выбранного филиала.", "История чата хранится на этом устройстве до закрытия вкладки.", "Кнопка с микрофоном рядом с полем — голосовой ввод: говорите, текст появится в поле, проверьте и отправьте."] },
        { t: "h", text: "Подсказки на страницах" },
        { t: "p", text: "На страницах «Отчёты», «Аналитика кафе» и «Склад» есть карточка **Совет Coca AI**. Нажмите «Получить совет» — помощник посмотрит данные этой страницы за выбранный период и даст 2–3 коротких совета. Готовый совет сохраняется: при следующем открытии он виден сразу и запрос не тратит." },
        { t: "h", text: "Сколько запросов в месяц" },
        { t: "p", text: "Каждый вопрос в чате и каждый новый совет — 1 запрос. Сколько запросов входит в тариф, видно в разделе [Подписка](/settings/subscription), остаток — под чатом и в карточке совета. Закончились — подключите пакет «+500 запросов к помощнику» или дождитесь следующего месяца. Если помощник не ответил из-за сбоя, запрос не засчитывается." },
      ],
    },
  ],
};
